import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Checkbox, Form, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { func, shape, string } from 'prop-types';
import { ColumnGroup, Column, Card } from '@cards/Card';

import { createOrgConfig, editOrgConfig, loadOrgConfig } from '@integrations/redux/actions';
import { selectIntegrationConfiguration } from '@integrations/redux/reducers';
import { loadStaffList } from '@integrations/kemtai/actions';
import { selectDirectChildOrganisationStaffList } from '@integrations/kemtai/reducers';
import './styles.less';

const detailsLayout = {
  labelCol: {
    span: 6,
  },
  layout: 'vertical',
  wrapperCol: {
    span: 18,
  },
};

function DirectChildOrganisationKemtaiConfig({
  form: { getFieldDecorator, getFieldValue, setFieldsValue, validateFieldsAndScroll },
  directChildOrganisation,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const requestedDirectChildOrganisationSlug = useRef();
  const requestedReplacementKey = useRef();
  const configuration = useSelector(
    selectIntegrationConfiguration(
      'kemtai',
      directChildOrganisation && directChildOrganisation.slug,
    ),
  );
  const { config: orgConfig, isSubmitting, status, errorStatus } = configuration;
  const [staffRequest, staffList] = useSelector(
    selectDirectChildOrganisationStaffList(directChildOrganisation && directChildOrganisation.slug),
  );

  useEffect(() => {
    if (
      !directChildOrganisation ||
      requestedDirectChildOrganisationSlug.current === directChildOrganisation.slug
    ) {
      return;
    }
    requestedDirectChildOrganisationSlug.current = directChildOrganisation.slug;
    dispatch(loadOrgConfig('kemtai', directChildOrganisation.slug));
  }, [directChildOrganisation, dispatch]);

  useEffect(() => {
    if (orgConfig && staffRequest.status === 'idle') {
      dispatch(loadStaffList(undefined, directChildOrganisation.slug));
    }
  }, [directChildOrganisation, dispatch, orgConfig, staffRequest.status]);

  useEffect(() => {
    if (staffList.length && !getFieldValue('defaultPatientOwnerId')) {
      setFieldsValue({ defaultPatientOwnerId: staffList[0].userId });
    }
  }, [getFieldValue, setFieldsValue, staffList]);

  const loadStaffForReplacementKey = value => {
    const apiKey = value.trim();
    if (apiKey && requestedReplacementKey.current !== apiKey) {
      requestedReplacementKey.current = apiKey;
      dispatch(loadStaffList(apiKey, directChildOrganisation.slug));
    }
  };

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      validateFieldsAndScroll((error, values) => {
        if (error) return;

        const replacementKey =
          typeof values.kemtaiAPIKey === 'string' ? values.kemtaiAPIKey.trim() : '';
        const data = {
          default_staff_owner_id: values.defaultPatientOwnerId,
        };
        if (replacementKey) data.api_key = replacementKey;

        if (orgConfig) {
          dispatch(editOrgConfig('kemtai', values.isEnabled, data, directChildOrganisation.slug));
        } else {
          dispatch(createOrgConfig('kemtai', data, directChildOrganisation.slug));
        }
      });
    },
    [directChildOrganisation, dispatch, validateFieldsAndScroll, orgConfig],
  );

  if (!directChildOrganisation || status === 'idle' || status === 'loading') {
    return <p role="status">{t('kemtai:organisationConfig.loadingConfiguration')}</p>;
  }

  if (status === 'failure') {
    const errorKeyByStatus = {
      403: 'loadingForbidden',
      404: 'loadingTargetNotFound',
      502: 'loadingUpstream',
    };
    const errorKey = errorKeyByStatus[errorStatus] || 'loadingError';

    return (
      <section role="alert">
        <p>{t(`integrations:organisationConfig.${errorKey}`)}</p>
        <button
          type="button"
          onClick={() => dispatch(loadOrgConfig('kemtai', directChildOrganisation.slug))}
        >
          {t('integrations:organisationConfig.retry')}
        </button>
      </section>
    );
  }

  return (
    <ColumnGroup className="edit-organisation-branding-details">
      <Column>
        <Card
          className="organisation-details-form-card"
          title={t('kemtai:organisationConfig.title')}
        >
          {orgConfig ? <p>{t('kemtai:organisationConfig.tokenConfigured')}</p> : null}
          <Form {...detailsLayout} onSubmit={handleSubmit}>
            <Form.Item
              className="details-item"
              label={t(
                orgConfig
                  ? 'kemtai:organisationConfig.replacementTokenLabel'
                  : 'kemtai:organisationConfig.tokenLabel',
              )}
              extra={t(
                orgConfig
                  ? 'kemtai:organisationConfig.replacementTokenHelpText'
                  : 'kemtai:organisationConfig.tokenHelpText',
              )}
            >
              {getFieldDecorator('kemtaiAPIKey', {
                rules: [
                  {
                    required: !orgConfig,
                    whitespace: !orgConfig,
                    message: t('kemtai:organisationConfig.tokenRequired'),
                  },
                ],
              })(
                <Input
                  type="password"
                  autoComplete="new-password"
                  onBlur={event => loadStaffForReplacementKey(event.target.value)}
                  allowClear={true}
                />,
              )}
            </Form.Item>
            <Form.Item
              className="details-item"
              label={t('kemtai:organisationConfig.defaultStaffLabel')}
              extra={t('kemtai:organisationConfig.defaultStaffHelpText')}
            >
              {getFieldDecorator('defaultPatientOwnerId', {
                rules: [
                  {
                    required: true,
                    message: t('kemtai:organisationConfig.defaultPatientOwnerIdRequired'),
                  },
                ],
                initialValue:
                  (orgConfig && orgConfig.data && orgConfig.data.default_staff_owner_id) ||
                  (staffList.length ? staffList[0].userId : undefined),
              })(
                <Select loading={staffRequest.isLoading}>
                  {staffList.map(staff => (
                    <Select.Option key={staff.userId} value={staff.userId}>
                      {staff.fullName}
                    </Select.Option>
                  ))}
                </Select>,
              )}
            </Form.Item>
            {orgConfig ? (
              <Form.Item
                className="details-item"
                label={t('kemtai:organisationConfig.isEnabled')}
                extra={t('kemtai:organisationConfig.isEnabledHelpText')}
              >
                {getFieldDecorator('isEnabled', {
                  initialValue: orgConfig.isEnabled,
                  valuePropName: 'checked',
                })(<Checkbox style={{ marginLeft: '0' }} />)}
              </Form.Item>
            ) : null}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={isSubmitting || staffRequest.isLoading}
              >
                {t('organisations:Branding.details.edit.save')}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Column>
    </ColumnGroup>
  );
}

DirectChildOrganisationKemtaiConfig.propTypes = {
  form: shape({
    getFieldDecorator: func.isRequired,
    getFieldValue: func.isRequired,
    setFieldsValue: func.isRequired,
    validateFieldsAndScroll: func.isRequired,
  }).isRequired,
  directChildOrganisation: shape({
    name: string.isRequired,
    slug: string.isRequired,
  }).isRequired,
};

export const DirectChildOrganisationKemtaiConfigForm = Form.create()(
  DirectChildOrganisationKemtaiConfig,
);

export default DirectChildOrganisationKemtaiConfigForm;
